import { Button } from "@svea/design-system-components-react";
import { FormHelperText, P, Text, useText } from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePageContext } from "../../hooks/context";
import {
	isAnyGuarantorsSelected,
	selectPossibleGuarantors,
	setPossibleGuarantors,
} from "../../slices/guarantorSlice";
import type { AppDispatch } from "../../store";
import type { AssignGuarantorEmailFormValues, GuarantorInteractive } from "../../types/guarantor";
import AddGuarantorBox from "./add-guarantor-box";
import AddGuarantorEmailModal from "./add-guarantor-email-modal";
import AddGuarantorModal from "./add-guarantor-modal";
import { filterCheckedGuarantors } from "./helpers";
import { useSignInformation } from "./hooks/hooks";

const AddGuarantorSection = () => {
	const dispatch = useDispatch<AppDispatch>();
	const [t, texts] = useText();

	const { applicationDetails, applicantContactInfo, submitAssignedGuarantors, guarantors } =
		usePageContext(useSignInformation);

	const [isAddGuarantorModalOpen, setIsAddGuarantorModalOpen] = useState(false);
	const [isAddGuarantorEmailModalOpen, setIsAddGuarantorEmailModalOpen] = useState(false);
	const [shouldDisplayNoGuarantorsChosenError, setShouldDisplayNoGuarantorsChosenError] =
		useState<boolean>(false);

	const applicantNationalId = applicationDetails.applicant.nationalId;

	const possibleGuarantors = useSelector(selectPossibleGuarantors);
	const hasSelectedGuarantors = useSelector(isAnyGuarantorsSelected);

	useEffect(() => {
		const missingRepresentatives = applicationDetails.organization.representatives.filter(
			(g) => !possibleGuarantors.find((pg) => g.nationalId === pg.nationalId),
		);

		dispatch(
			setPossibleGuarantors([
				...missingRepresentatives.map((mr) => ({
					checked: !!guarantors.find((g) => g.nationalId === mr.nationalId),
					guarantorType: "representative",
					email: mr.nationalId === applicantNationalId ? applicantContactInfo?.email : null,
					isApplicant: true,
					nationalId: mr.nationalId,
					names: mr.names,
				})),
				...possibleGuarantors,
			]),
		);
	}, [applicationDetails, guarantors]);

	const handleAddGuarantor = (newGuarantor: GuarantorInteractive) => {
		if (!possibleGuarantors.find((item) => item.nationalId === newGuarantor.nationalId)) {
			dispatch(setPossibleGuarantors([...possibleGuarantors, newGuarantor]));
		}
	};

	const handleOpenAddGuarantorEmailModal = (toggle: boolean) => {
		if (hasSelectedGuarantors) {
			setShouldDisplayNoGuarantorsChosenError(false);
			setIsAddGuarantorEmailModalOpen(toggle);
		} else {
			setShouldDisplayNoGuarantorsChosenError(true);
		}
	};

	const addEmailAndSubmitGuarantors = async (values: AssignGuarantorEmailFormValues) => {
		const alteredPossibleGuarantors = possibleGuarantors.map((guarantor) => {
			const incomingValue = values.possibleGuarantors.find(
				(item) => item.nationalId === guarantor.nationalId,
			);
			return incomingValue || guarantor;
		});

		dispatch(setPossibleGuarantors(alteredPossibleGuarantors));
		await submitAssignedGuarantors(
			filterCheckedGuarantors(alteredPossibleGuarantors).filter(
				(pg) => !guarantors.find((g) => g.nationalId === pg.nationalId),
			),
		);
		setIsAddGuarantorEmailModalOpen(false);
	};

	return (
		<div className="pb-6">
			<div className="pb-4">
				<P>
					{t(texts.signPage.guarantors.description)}
					<Text bold>{t(texts.signPage.signing.guarantorSign.descriptionBold)}</Text>
				</P>
			</div>
			<div>
				<AddGuarantorBox applicantNationalId={applicationDetails.applicant.nationalId} />

				{shouldDisplayNoGuarantorsChosenError && (
					<FormHelperText error>
						{t(texts.signPage.guarantors.noGuarantorChosenError)}
					</FormHelperText>
				)}

				<div className="flex justify-start pt-4">
					<Button
						onPress={() => {
							setIsAddGuarantorModalOpen(true);
						}}
						iconStart="plusCircle"
						variant="plain"
						size="md"
					>
						<Text>{t(texts.signPage.addGuarantorModal.addAnotherPerson)}</Text>
					</Button>
				</div>
			</div>
			<div className="flex justify-end pt-4">
				<Button
					aria-label="add, button"
					variant="primary"
					onPress={() => {
						handleOpenAddGuarantorEmailModal(true);
					}}
					isPending={isAddGuarantorEmailModalOpen}
				>
					{t(texts.signPage.addGuarantorModal.chooseButton)}
				</Button>
			</div>
			<AddGuarantorModal
				isModalOpen={isAddGuarantorModalOpen}
				setOpen={setIsAddGuarantorModalOpen}
				handleAdd={handleAddGuarantor}
			/>
			<AddGuarantorEmailModal
				applicantNationalId={applicantNationalId}
				setOpen={setIsAddGuarantorEmailModalOpen}
				handleSend={addEmailAndSubmitGuarantors}
				isOpen={isAddGuarantorEmailModalOpen}
			/>
		</div>
	);
};

export default AddGuarantorSection;
